import { useEffect } from 'react'
import { useFormContext, useFieldArray } from 'react-hook-form'
import { z } from 'zod'
import { useAtom } from 'jotai'
import { currentStep } from '@/app/store'
import { useCrefazContext } from '@/app/contexts/CrefazContext'
import { useCustomerCpf } from '@/app/hooks/useCustomerCpf'
import { BasicInput } from '@/view/components/BasicInput'
import { BasicSelect } from '@/view/components/BasicSelect'
import { Button } from '@/view/components/ui/button'
import masks from '@/app/utils/masks'
import { phoneMask } from '@/app/utils/phoneFormater'
import { useRelationshipDegree } from '@/app/hooks/useRelationshipDegree'

export const contactReferencesSchema = z.object({
  referenceContact: z
    .array(
      z.object({
        degree: z.string().min(1, '* obrigatório'),
        name: z.string().min(2, '* obrigatório'),
        phone: z
          .string()
          .transform((val) => val.replace(/\D/g, ''))
          .refine((val) => val.length === 11, '* telefone inválido'),
      }),
    )
    .min(2, 'Informe dois contatos')
    .max(2, 'Informe no máximo dois contatos'),
})

interface ContactReferencesFormProps {
  visible?: boolean
}
export default function ContactReferencesForm({
  visible,
}: ContactReferencesFormProps) {
  const [, setStep] = useAtom(currentStep)
  const { customerData } = useCrefazContext()
  const { data: isCustomer } = useCustomerCpf(customerData?.cpf)

  const { data: relationshipDegreeData } = useRelationshipDegree()

  const relationshipDegreeOptions =
    relationshipDegreeData?.data.map((relationship) => ({
      label: relationship.nome,
      value: String(relationship.id),
    })) ?? []

  const {
    control,
    trigger,
    formState: { errors },
  } = useFormContext()

  const { fields, replace } = useFieldArray({
    control,
    name: 'contactReferences.referenceContact',
  })

  useEffect(() => {
    if (
      fields.length === 0 &&
      (!isCustomer?.referenceContact ||
        isCustomer.referenceContact.length === 0)
    ) {
      replace([
        { degree: '', name: '', phone: '' },
        { degree: '', name: '', phone: '' },
      ])
    }
  }, [fields.length, replace, isCustomer])

  useEffect(() => {
    if (
      isCustomer?.referenceContact &&
      isCustomer.referenceContact.length === 2
    ) {
      const formattedReferences = isCustomer.referenceContact.map(
        (ref: any) => ({
          degree: ref.degree ?? '',
          name: ref.name ?? '',
          phone: masks.phone(ref.phone ?? ''),
        }),
      )

      replace(formattedReferences)
    }
  }, [isCustomer])

  const handleNext = async () => {
    const isValid = await trigger('contactReferences')
    if (isValid) {
      setStep((state) => state + 1)
    }
  }

  return (
    <div className={visible ? 'flex flex-col gap-8' : 'hidden'}>
      <div className="flex flex-col gap-8">
        {fields.map((field, index) => (
          <div key={field.id} className="grid grid-cols-3 gap-4">
            <BasicSelect
              name={`contactReferences.referenceContact.${index}.degree`}
              label="Grau de parentesco"
              options={relationshipDegreeOptions}
              placeholder="Selecione"
              error={
                (errors.contactReferences as any)?.referenceContact?.[index]
                  ?.degree?.message
              }
            />
            <BasicInput
              name={`contactReferences.referenceContact.${index}.name`}
              label="Nome"
              error={
                (errors.contactReferences as any)?.referenceContact?.[index]
                  ?.name?.message
              }
            />
            <BasicInput
              name={`contactReferences.referenceContact.${index}.phone`}
              label="Telefone"
              error={
                (errors.contactReferences as any)?.referenceContact?.[index]
                  ?.phone?.message
              }
              onInput={phoneMask}
              maxLength={15}
            />
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-5">
        <Button
          type="button"
          className="rounded-full bg-white02 px-8 py-3 font-semibold text-primary hover:bg-white02"
          onClick={() => setStep((state) => state - 1)}
        >
          Voltar
        </Button>
        <Button
          className="rounded-full px-8 py-3 font-semibold text-white"
          type="button"
          onClick={handleNext}
        >
          Continuar
        </Button>
      </div>
    </div>
  )
}
